import { Check, ChevronRight, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { SimpleTooltip } from '@/components/SimpleTooltip';
import { JOURNEY_STEPS } from './types';

/**
 * ENG-1744 (the shared status→tone vocabulary) deliberately stops at this file.
 * The strip is a **stepper**, and none of its three colour decisions is a
 * status:
 *
 *   • `done` is `bg-foreground` — a finished step is neutral ink, not green.
 *     Toning it would repaint the entire done-line black → success.
 *   • the current step is a filled `bg-primary` pill (inline) or an *outlined*
 *     `border-info` disc (full). "You are here" is not a state of the work, and
 *     an outlined marker has no equivalent among the module's four roles (chip,
 *     fill, text, banner) — the fill role would turn the disc into a tint.
 *   • the markers and the re-mesh badge pair a solid fill with a matching
 *     `-foreground` glyph, which the fill role does not cover: dots, bars and
 *     grid cells carry no glyph, so they need no paired foreground.
 *
 * Inventing a stepper role to absorb three call sites would therefore change
 * how all three look. Left declared here on purpose.
 */

/**
 * Resolve a step's translated label/sub from its stable id; unknown ids fall
 * back to the literal label/sub carried on the step (e.g. custom steps passed
 * by a caller).
 *
 * `JOURNEY_STEPS` names its third step `results`. This map only knew
 * `analyze`, so that step fell through to its English literal on every locale
 * — "網格 › 模擬 › Results" in the study header, all through drafting and the
 * upload-from-study path (ENG-3097). The labels are the study header's own
 * phase names, so this strip and the Results-page breadcrumb
 * (`AnalyzePhaseBreadcrumb`) say the same words.
 */
const STEP_LABEL_KEY = {
  mesh: 'simopsShared.studyHeader.phaseMesh',
  simulate: 'simopsShared.studyHeader.phaseSimulate',
  results: 'simopsShared.studyHeader.phaseAnalyze',
  analyze: 'simopsShared.studyHeader.phaseAnalyze',
} as const;

const STEP_SUB_KEY = {
  mesh: 'simopsMeshing.journey.stepMeshSub',
  simulate: 'simopsMeshing.journey.stepSimulateSub',
  results: 'simopsMeshing.journey.stepResultsSub',
  analyze: 'simopsMeshing.journey.stepResultsSub',
} as const;

function stepLabel(t: TFunction, step: JourneyStripStep): string {
  const key = STEP_LABEL_KEY[step.id as keyof typeof STEP_LABEL_KEY];
  return key ? t(key) : step.label;
}

function stepSub(t: TFunction, step: JourneyStripStep): string | null {
  const key = STEP_SUB_KEY[step.id as keyof typeof STEP_SUB_KEY];
  return key ? t(key) : step.sub;
}

export interface JourneyStripStep {
  id: string;
  label: string;
  sub: string | null;
}

interface JourneyStripProps {
  /** Index (0-based) of the currently active step. */
  current: number;
  /** Index of the first step that is NOT yet done. Steps `< doneIdx` render as done. */
  doneIdx: number;
  /** Steps to render. Defaults to the SimOps macro journey. */
  steps?: readonly JourneyStripStep[];
  /** Side state — N DPs re-meshing. Renders a badge on the Mesh step. */
  remeshing?: number;
  /** Replaces the active step's sub-text. Defaults to "you are here". */
  branches?: string;
  /** Step ids that were intentionally bypassed by the selected workflow. */
  skippedStepIds?: readonly string[];
  /** When provided, each step becomes a button that calls back with its index. */
  onStepClick?: (index: number) => void;
  /**
   * Compact one-row presentation for the study header: a borderless inline
   * dot stepper with a filled active marker and no sublabels, vs. the default
   * full-width bordered card. Same step logic, denser chrome.
   */
  inline?: boolean;
}

type StepState = 'done' | 'active' | 'future' | 'skipped';

/**
 * Invariant: callers should pass `doneIdx >= current` so the active step is
 * never behind the done line. Under that invariant the `i < current` case is
 * already covered by `i < doneIdx`, so only three branches are needed.
 */
function stepStateFor(i: number, current: number, doneIdx: number): StepState {
  if (i < doneIdx) return 'done';
  if (i === current) return 'active';
  return 'future';
}

interface StepMarkerProps {
  state: StepState;
  index: number;
  /** `full` = bordered card (outline active); `inline` = dense filled active. */
  variant: 'full' | 'inline';
  remeshing: number;
  isMesh: boolean;
}

/**
 * The numbered step circle + re-mesh badge — shared by both the full bordered
 * strip and the compact inline strip. Only sizing and the active treatment
 * (outline vs. filled) differ by `variant`.
 */
function StepMarker({
  state,
  index,
  variant,
  remeshing,
  isMesh,
}: StepMarkerProps) {
  const { t } = useTranslation();
  const inline = variant === 'inline';
  const iconSize = inline ? 13 : 14;
  return (
    <div className="relative flex-shrink-0">
      <div
        // Stepper colours, not status tones — see the note at the top of this
        // file (ENG-1744).
        className={cn(
          'flex items-center justify-center rounded-full border text-2xs font-bold',
          inline ? 'h-6 w-6' : 'h-7 w-7',
          state === 'done' && 'border-foreground bg-foreground text-background',
          state === 'active' &&
            (inline
              ? 'border-info bg-info text-info-foreground'
              : 'border-info bg-background text-info'),
          state === 'future' &&
            'border-border bg-background text-muted-foreground',
          state === 'skipped' && 'border-border bg-muted text-muted-foreground',
        )}
        aria-current={state === 'active' ? 'step' : undefined}
      >
        {state === 'done' ? (
          <Check size={iconSize} strokeWidth={2.5} />
        ) : state === 'skipped' ? (
          <Minus size={iconSize} strokeWidth={2.5} />
        ) : (
          index + 1
        )}
      </div>
      {isMesh && remeshing > 0 && (
        <SimpleTooltip
          content={t('simopsMeshing.journey.remeshTooltip', {
            count: remeshing,
          })}
        >
          {/* A count badge with a paired `-foreground` glyph, in both strip
              variants — outside the tone vocabulary's fill role (ENG-1744;
              see the note at the top of this file). */}
          <span className="absolute -top-1.5 -right-2.5 rounded-full border-2 border-card bg-warning px-1 font-mono text-4xs font-bold text-warning-foreground tabular-nums">
            {t('simopsMeshing.journey.remeshBadge', { count: remeshing })}
          </span>
        </SimpleTooltip>
      )}
    </div>
  );
}

export function JourneyStrip({
  current,
  doneIdx,
  steps = JOURNEY_STEPS,
  remeshing = 0,
  branches,
  skippedStepIds = [],
  onStepClick,
  inline = false,
}: JourneyStripProps) {
  const { t } = useTranslation();
  const skipped = new Set(skippedStepIds);

  if (inline) {
    return (
      <InlineJourneyStrip
        current={current}
        doneIdx={doneIdx}
        steps={steps}
        remeshing={remeshing}
        branches={branches}
        skipped={skipped}
        onStepClick={onStepClick}
      />
    );
  }

  return (
    // ENG-1742: `<Card>` is the panel-shell primitive and already spells this
    // strip's geometry exactly — `rounded-xl border bg-card` — so only the
    // gutters, the row layout and `shadow-none` (Card ships a `shadow`; this
    // strip never had one) are left at the call site. `role="list"` and the
    // strip's aria-label pass straight through Card's prop spread, so the
    // stepper's semantics are unchanged.
    <Card
      role="list"
      aria-label={t('simopsMeshing.journey.ariaLabel')}
      className="flex items-stretch overflow-hidden px-1.5 py-3.5 shadow-none"
    >
      {steps.map((step, i) => {
        const state = skipped.has(step.id)
          ? 'skipped'
          : stepStateFor(i, current, doneIdx);
        const isMesh = step.id === 'mesh';
        const isLast = i === steps.length - 1;
        const clickable = onStepClick != null;

        return (
          <div
            key={step.id}
            role="listitem"
            className={cn(
              'flex min-w-0 flex-1 items-center gap-3 px-4.5',
              clickable && 'cursor-pointer rounded-md hover:bg-muted/60',
            )}
            onClick={clickable ? () => onStepClick(i) : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onStepClick(i);
                    }
                  }
                : undefined
            }
            tabIndex={clickable ? 0 : undefined}
          >
            <StepMarker
              state={state}
              index={i}
              variant="full"
              remeshing={remeshing}
              isMesh={isMesh}
            />
            <div className="min-w-0 flex-1">
              <div
                className={cn(
                  'text-xs leading-tight whitespace-nowrap',
                  state === 'active' ? 'font-semibold' : 'font-medium',
                  state === 'future' || state === 'skipped'
                    ? 'text-muted-foreground'
                    : 'text-foreground',
                )}
              >
                {stepLabel(t, step)}
              </div>
              <div className="mt-0.5 text-3xs leading-tight whitespace-nowrap text-muted-foreground">
                {state === 'skipped'
                  ? t('simopsMeshing.journey.skipped')
                  : state === 'active'
                    ? (branches ?? t('simopsMeshing.journey.youAreHere'))
                    : stepSub(t, step)}
              </div>
            </div>
            {!isLast && (
              <ChevronRight
                size={14}
                className="text-muted-foreground"
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </Card>
  );
}

interface InlineJourneyStripProps {
  current: number;
  doneIdx: number;
  steps: readonly JourneyStripStep[];
  remeshing: number;
  branches?: string;
  skipped: Set<string>;
  onStepClick?: (index: number) => void;
}

/**
 * One-row presentation for the study header: compact pills in the same visual
 * language as the cross-product `ProductPipelineStrip` (rounded-full chips,
 * active = primary fill, chevron separators) so the two strips read as one
 * system. Shares the done/active/future/skipped logic with the full strip.
 */
function InlineJourneyStrip({
  current,
  doneIdx,
  steps,
  remeshing,
  branches,
  skipped,
  onStepClick,
}: InlineJourneyStripProps) {
  const { t } = useTranslation();
  const clickable = onStepClick != null;
  return (
    <div
      className="flex items-center gap-1"
      role="list"
      aria-label={t('simopsMeshing.journey.ariaLabel')}
    >
      {steps.map((step, i) => {
        const state = skipped.has(step.id)
          ? 'skipped'
          : stepStateFor(i, current, doneIdx);
        const isMesh = step.id === 'mesh';
        const isLast = i === steps.length - 1;

        return (
          <div
            key={step.id}
            className="flex items-center gap-1"
            role="listitem"
          >
            <div className="relative">
              {/* ENG-1742: this pill IS the Badge recipe — `rounded-full`
                  `px-2 py-0.5`, a `text-2xs` rung and a `bg-primary` fill for
                  the active step — so it composes `<Badge>` rather than
                  respelling it. `size="sm"` is the `text-2xs` rung and
                  `variant="default"` is byte-for-byte the active fill; the
                  three quiet states take `variant="ghost"` (whose only
                  declarations are `[a&]:` anchor hovers, inert on a span) plus
                  this stepper's own text colour and hover.
                  `transition-colors` is restored on purpose: Badge's base
                  spells `transition-[color,box-shadow]`, which does not
                  animate `background-color`, so without it the `hover:bg-muted`
                  on the done/future pills would snap instead of fade.
                  The one accepted deviation is glyph size: Badge normalises
                  its direct `svg` children to `size-3`, and a CSS rule beats
                  lucide's `width`/`height` attributes, so the check/minus now
                  measures 12px — the `size={12}` below just makes the source
                  say what already renders.
                  Stepper colours, not status tones — see the note at the top
                  of this file (ENG-1744). */}
              <Badge
                size="sm"
                variant={state === 'active' ? 'default' : 'ghost'}
                className={cn(
                  // `border-0`: the base recipe carries `border
                  // border-transparent` and the `<div>` pill this replaces had
                  // no border, so without this every step pill grows 2px in
                  // both axes.
                  'border-0 transition-colors',
                  state === 'done' &&
                    cn('text-foreground', clickable && 'hover:bg-muted'),
                  (state === 'future' || state === 'skipped') &&
                    cn(
                      'text-muted-foreground',
                      clickable && 'hover:bg-muted hover:text-foreground',
                    ),
                )}
                aria-current={state === 'active' ? 'step' : undefined}
                onClick={clickable ? () => onStepClick(i) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onStepClick(i);
                        }
                      }
                    : undefined
                }
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
              >
                {state === 'done' && (
                  <Check size={12} strokeWidth={2.5} aria-hidden />
                )}
                {state === 'skipped' && (
                  <Minus size={12} strokeWidth={2.5} aria-hidden />
                )}
                {stepLabel(t, step)}
                {state === 'active' && branches && (
                  <span className="text-3xs font-medium text-primary-foreground">
                    · {branches}
                  </span>
                )}
              </Badge>
              {isMesh && remeshing > 0 && (
                <SimpleTooltip
                  content={t('simopsMeshing.journey.remeshTooltip', {
                    count: remeshing,
                  })}
                >
                  <span className="absolute -top-1.5 -right-2 rounded-full border-2 border-card bg-warning px-1 font-mono text-4xs font-bold text-warning-foreground tabular-nums">
                    {t('simopsMeshing.journey.remeshBadge', {
                      count: remeshing,
                    })}
                  </span>
                </SimpleTooltip>
              )}
            </div>
            {!isLast && (
              <ChevronRight
                size={12}
                className="text-muted-foreground"
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
